import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Camera, X, Move, ZoomIn, ZoomOut, RotateCw, Download, Info } from 'lucide-react';

/**
 * ARDoorPreview - lightweight "AR-lite" camera overlay.
 * Uses HTML5 getUserMedia to show the live camera feed and lets the
 * customer drag/scale the chosen CriLam door image over their real doorway.
 * Snapshot exports a composite PNG (with CriLam brand chip).
 */
const ARDoorPreview = ({ overlayImageUrl, onClose, brandLabel = 'CriLam · Preview in Your Room' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(0.65);
  const [rotate, setRotate] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (e) {
        setError('Camera access denied or unavailable. You can still preview using an uploaded photo.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [stream]);

  const startDrag = useCallback((clientX, clientY) => {
    dragRef.current = { dragging: true, startX: clientX, startY: clientY, baseX: pos.x, baseY: pos.y };
  }, [pos]);

  const onMove = useCallback((clientX, clientY) => {
    if (!dragRef.current.dragging) return;
    const dx = clientX - dragRef.current.startX;
    const dy = clientY - dragRef.current.startY;
    setPos({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy });
  }, []);

  const endDrag = useCallback(() => { dragRef.current.dragging = false; }, []);

  useEffect(() => {
    const mm = (e) => onMove(e.clientX, e.clientY);
    const tm = (e) => e.touches[0] && onMove(e.touches[0].clientX, e.touches[0].clientY);
    const up = () => endDrag();
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', tm, { passive: true });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', tm);
      window.removeEventListener('touchend', up);
    };
  }, [onMove, endDrag]);

  const captureSnapshot = async () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    // Camera feed
    ctx.drawImage(video, 0, 0, w, h);

    // Overlay image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = overlayImageUrl;
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const targetW = w * scale;
    const targetH = (img.height / img.width) * targetW;
    const cx = w / 2 + (pos.x / window.innerWidth) * w;
    const cy = h / 2 + (pos.y / window.innerHeight) * h;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.drawImage(img, -targetW / 2, -targetH / 2, targetW, targetH);
    ctx.restore();

    // CriLam brand chip (mandatory branding on shared image)
    ctx.fillStyle = 'rgba(10,13,12,0.8)';
    ctx.fillRect(w - 320, h - 44, 300, 30);
    ctx.fillStyle = '#D4AF37';
    ctx.font = 'bold 14px Arial';
    ctx.fillText('CriLam · AI Design Studio', w - 310, h - 24);

    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'crilam-ar-preview.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black" data-testid="ar-door-preview">
      <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
      <canvas ref={canvasRef} className="hidden" />

      {/* Overlay image (draggable) */}
      {!error && (
        <motion.img
          src={overlayImageUrl}
          alt="Door preview"
          onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
          onTouchStart={(e) => e.touches[0] && startDrag(e.touches[0].clientX, e.touches[0].clientY)}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px)) scale(${scale}) rotate(${rotate}deg)`,
            transformOrigin: 'center',
            maxWidth: '80vw',
            maxHeight: '80vh',
            cursor: 'grab',
            userSelect: 'none',
            filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.5))',
          }}
          draggable={false}
        />
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center p-8 text-center">
          <div className="max-w-md bg-black/70 backdrop-blur-md rounded-xl p-6 text-white">
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Top brand chip */}
      <div className="absolute top-4 left-4 right-4 flex items-start justify-between pointer-events-none">
        <div className="pointer-events-auto px-3 py-1.5 bg-[#0A0D0C]/80 backdrop-blur text-[#D4AF37] text-xs font-bold rounded-full">
          {brandLabel}
        </div>
        <button
          onClick={onClose}
          className="pointer-events-auto p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
          aria-label="Close AR view"
          data-testid="ar-close-btn"
        >
          <X className="h-5 w-5 text-gray-800" />
        </button>
      </div>

      {/* Info hint */}
      <div className="absolute top-16 left-1/2 -translate-x-1/2 max-w-md text-center px-3 py-1.5 bg-black/60 backdrop-blur text-white text-xs rounded-full">
        <Info className="h-3.5 w-3.5 inline mr-1" />
        Drag to move · Pinch or use buttons to scale
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
        <div className="max-w-md mx-auto flex items-center justify-around gap-3">
          <button
            onClick={() => setScale((s) => Math.max(0.15, s - 0.08))}
            className="p-3 bg-white/95 rounded-full hover:bg-[#D4AF37] transition-colors"
            aria-label="Zoom out"
            data-testid="ar-zoom-out"
          >
            <ZoomOut className="h-5 w-5 text-[#004534]" />
          </button>
          <button
            onClick={() => setScale((s) => Math.min(1.6, s + 0.08))}
            className="p-3 bg-white/95 rounded-full hover:bg-[#D4AF37] transition-colors"
            aria-label="Zoom in"
            data-testid="ar-zoom-in"
          >
            <ZoomIn className="h-5 w-5 text-[#004534]" />
          </button>
          <button
            onClick={() => setRotate((r) => r + 15)}
            className="p-3 bg-white/95 rounded-full hover:bg-[#D4AF37] transition-colors"
            aria-label="Rotate"
            data-testid="ar-rotate"
          >
            <RotateCw className="h-5 w-5 text-[#004534]" />
          </button>
          <button
            onClick={() => setPos({ x: 0, y: 0 })}
            className="p-3 bg-white/95 rounded-full hover:bg-[#D4AF37] transition-colors"
            aria-label="Recenter"
            data-testid="ar-recenter"
          >
            <Move className="h-5 w-5 text-[#004534]" />
          </button>
          <button
            onClick={captureSnapshot}
            disabled={!!error}
            className="px-4 py-3 bg-[#D4AF37] text-[#004534] rounded-full font-bold text-sm hover:bg-white transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="ar-capture-btn"
          >
            <Download className="h-4 w-4" />
            Save Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default ARDoorPreview;
